import { useEffect, useMemo, useRef, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { DateTime } from "luxon";
import theme from "../../../theme";
import { Box } from "../../../components/Box";
import { Image } from "../../../components/Image";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import CustomTabs from "../../../components/CustomTabs";
import {
  HiOutlineSquares2X2,
  HiOutlineUser,
  HiOutlineBuildingOffice2,
} from "react-icons/hi2";
import Select from "../../../components/Select";
import { Toggle } from "../../../components/Toggle";
import editIcon from "../../../assets/settings/edit.svg";
import { Slider } from "../../../components/Slider";
import { Input } from "../../../components/Input";
import { BiGridVertical } from "react-icons/bi";
import { useGetSectors, type Sector } from "../../../data/useGetSectors";
import { queryClient } from "../../../queryClient";
import { notify } from "../../../utils/notify";
import { showError } from "../../../utils/error-handling";
import { useCreateSector } from "../../../data/useCreateSector";
import { useDeleteSector } from "../../../data/useDeleteSector";
import { useEditSector } from "../../../data/useEditSector";
import { useUpdateSectorOrder } from "../../../data/useUpdateSectorOrder";
import { Modal } from "../../../components/Modal";

type Scope = "all" | "individual" | "entity";

type SectorRow = Sector & {
  order?: number | null;
  _type?: "individual" | "entity";
};

type DragState = {
  draggingId: SectorRow["id"] | null;
  overId: SectorRow["id"] | null;
};

const COLS = "minmax(320px,1fr) 200px 160px 120px 120px";
const baseRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: COLS,
  alignItems: "center",
  columnGap: 24,
};
const headStyle: React.CSSProperties = {
  ...baseRow,
  padding: "10px 16px",
  color: theme.colors.muted,
};
const bodyRowStyle: React.CSSProperties = {
  ...baseRow,
  padding: "14px 16px",
  borderTop: "1px solid #F3F4F6",
};

type RiskLower = "low" | "medium" | "high";

const riskOptions = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
] as const;

const toLowerRisk = (v?: string): RiskLower =>
  (v?.toLowerCase() as RiskLower) ?? "medium";

const formatLastUpdated = (value?: string) => {
  if (!value) return "-";
  const asISO = DateTime.fromISO(value);
  if (asISO.isValid) return asISO.toFormat("dd LLL yyyy");
  const asSQL = DateTime.fromSQL(value);
  if (asSQL.isValid) return asSQL.toFormat("dd LLL yyyy");
  return "-";
};

const RiskPill = ({ value }: { value?: string }) => {
  const v = toLowerRisk(value);
  const label = v === "low" ? "Low" : v === "high" ? "High" : "Medium";
  const riskLevel = v === "low" ? "low" : v === "high" ? "high" : "medium";
  const bg = theme.colors.risk[riskLevel].bg;
  const color = theme.colors.risk[riskLevel].color;
  return (
    <Paragraph
      style={{
        display: "inline-block",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 600,
        background: bg,
        color,
        minWidth: "auto",
        textAlign: "center",
      }}
    >
      {label}
    </Paragraph>
  );
};

const Row = ({
  item,
  onToggleActive,
  onEdit,
  draggableProps,
  isDragging,
  isToggling,
}: {
  item: SectorRow;
  onToggleActive: (id: SectorRow["id"], next: boolean) => void;
  onEdit: (item: SectorRow) => void;
  draggableProps: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
  };
  isDragging: boolean;
  isToggling: boolean;
}) => {
  return (
    <div
      style={{
        ...bodyRowStyle,
        background: isDragging ? "#F9FAFB" : "transparent",
        cursor: "grab",
      }}
      {...draggableProps}
    >
      <Flex alignItems="center" gap="12">
        <BiGridVertical size={18} style={{ opacity: 0.7 }} />
        <Paragraph fontSize="2" fontWeight="600">
          {item.name}
        </Paragraph>
      </Flex>

      <RiskPill value={(item as any).risk_level} />

      <Paragraph color="gray.80">
        {formatLastUpdated((item as any).updated_at)}
      </Paragraph>

      <Image
        src={editIcon}
        alt="edit icon"
        height="24px"
        width="24px"
        style={{ cursor: "pointer" }}
        onClick={() => onEdit(item)}
      />

      <Flex alignItems="center">
        <Toggle
          isChecked={Boolean(item.active)}
          onChange={(e: any) => onToggleActive(item.id, e.target.checked)}
          mt={2}
          isDisabled={isToggling}
        />
      </Flex>
    </div>
  );
};

const SettingsPropertiesSectors = () => {
  const [activeTab, setActiveTab] = useState(0);
  const scope: Scope =
    activeTab === 0 ? "all" : activeTab === 1 ? "individual" : "entity";

  const { data, isLoading, isError } = useGetSectors(scope);

  const { mutate: deleteSector } = useDeleteSector();
  const { mutate: createSector } = useCreateSector();
  const { mutate: editSector } = useEditSector();
  const { mutate: updateSectorOrder } = useUpdateSectorOrder();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"add" | "edit">("add");
  const [editingId, setEditingId] = useState<SectorRow["id"] | null>(null);
  const [formName, setFormName] = useState("");
  const [formRisk, setFormRisk] = useState<RiskLower>("medium");
  const [formActive, setFormActive] = useState<boolean>(true);
  const [formType, setFormType] = useState<"individual" | "entity">(
    scope === "entity" ? "entity" : "individual"
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [doubleClickOnDelete, setDoubleClickOnDelete] = useState(false);

  const [list, setList] = useState<SectorRow[]>([]);
  const [togglingId, setTogglingId] = useState<SectorRow["id"] | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  const items = useMemo<SectorRow[]>(
    () => (Array.isArray(data) ? (data as SectorRow[]) : []),
    [data]
  );
  useEffect(() => setList(items), [items]);

  const typeOptions = [
    { label: "Individual", value: "individual" },
    { label: "Entity", value: "entity" },
  ] as const;

  const openAdd = () => {
    setFormMode("add");
    setEditingId(null);
    setFormName("");
    setFormRisk("medium");
    setFormActive(true);
    setFormType(
      scope === "entity"
        ? "entity"
        : scope === "individual"
        ? "individual"
        : "individual"
    );
    setIsFormOpen(true);
  };

  const openEdit = (item: SectorRow) => {
    setFormMode("edit");
    setEditingId(item.id);
    setFormName(item.name ?? "");
    setFormRisk(toLowerRisk((item as any).risk_level as any));
    setFormActive(Boolean(item.active));
    setFormType((item._type as any) === "entity" ? "entity" : "individual");
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (isSubmitting) return;
    setIsFormOpen(false);
  };

  const invalidateLists = () => {
    queryClient.invalidateQueries({
      queryKey: ["business_sector_risk_level", scope],
    });
  };

  const onComplete = () => {
    invalidateLists();
    setIsSubmitting(false);
    setIsFormOpen(false);
    setIsDeleteModalOpen(false);
    setDoubleClickOnDelete(false);
  };

  const onError = (err: any, msg: string) => {
    setIsSubmitting(false);
    showError(err, msg);
    setIsDeleteModalOpen(false);
    setDoubleClickOnDelete(false);
  };

  const onSuccess = (msg: string) => {
    notify(msg, { type: "success", position: "bottom-left" });
    onComplete();
  };

  const handleCreate = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      name: formName.trim(),
      risk_level: formRisk,
      active: formActive,
      _type: formType,
    } as any;

    createSector(payload, {
      onSuccess: () => onSuccess("Business sector added successfully!"),
      onError: (err) =>
        onError(err, "A problem occurred while creating the Business sector."),
    });
  };

  const handleUpdate = () => {
    if (!editingId || isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      id: editingId,
      params: {
        name: formName.trim(),
        risk_level: formRisk,
        active: formActive,
        _type: formType,
      },
    } as any;

    editSector(payload, {
      onSuccess: () => onSuccess("Business sector edited successfully!"),
      onError: (err) =>
        onError(err, "A problem occurred while updating the Business sector."),
    });
  };

  const handleDelete = () => {
    if (!editingId || isSubmitting) return;
    setIsSubmitting(true);
    setDoubleClickOnDelete(true);
    deleteSector({ id: editingId } as any, {
      onSuccess: () => onSuccess("Business sector deleted successfully!"),
      onError: (err) =>
        onError(err, "A problem occurred while deleting the Business sector."),
    });
  };

  const dragState = useRef<DragState>({ draggingId: null, overId: null });
  const reorderByIds = (
    arr: SectorRow[],
    startId: SectorRow["id"],
    endId: SectorRow["id"]
  ) => {
    const startIndex = arr.findIndex((x) => x.id === startId);
    const endIndex = arr.findIndex((x) => x.id === endId);
    if (startIndex === -1 || endIndex === -1 || startIndex === endIndex)
      return arr;
    const next = [...arr];
    const [moved] = next.splice(startIndex, 1);
    next.splice(endIndex, 0, moved);
    return next;
  };

  const handleDragStart = (id: SectorRow["id"]) => (e: React.DragEvent) => {
    dragState.current = { draggingId: id, overId: id };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(id));
  };
  const handleDragOver = (id: SectorRow["id"]) => (e: React.DragEvent) => {
    e.preventDefault();
    dragState.current.overId = id;
  };
  const handleDragEnter = (id: SectorRow["id"]) => (e: React.DragEvent) => {
    e.preventDefault();
    dragState.current.overId = id;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const { draggingId, overId } = dragState.current;
    if (!draggingId || !overId || draggingId === overId) return;

    const visibleIds = list.map((x) => x.id);
    if (!visibleIds.includes(draggingId) || !visibleIds.includes(overId))
      return;

    const reordered = reorderByIds(list, draggingId, overId);
    setList(reordered);

    setIsReordering(true);
    updateSectorOrder(
      {
        orders: reordered.map((row, idx) => ({ id: row.id, order: idx })),
      },
      {
        onSuccess: () => {
          notify("Order updated!", {
            type: "success",
            position: "bottom-left",
          });
          setIsReordering(false);
          invalidateLists();
        },
        onError: (err) => {
          setIsReordering(false);
          showError(err, "Failed to update order.");
          invalidateLists();
        },
      }
    );

    dragState.current = { draggingId: null, overId: null };
  };

  const handleDragEnd = () =>
    (dragState.current = { draggingId: null, overId: null });

  const onToggleActive = (id: SectorRow["id"], next: boolean) => {
    const target = list.find((r) => r.id === id);
    if (!target) return;

    setList((prev) =>
      prev.map((r) => (r.id === id ? { ...r, active: next } : r))
    );
    setTogglingId(id);

    editSector(
      {
        id,
        params: {
          name: target.name,
          risk_level: toLowerRisk((target as any).risk_level as any),
          active: next,
          _type: (target._type as any) === "entity" ? "entity" : "individual",
        },
      } as any,
      {
        onSuccess: () => {
          notify(
            `Business sector ${
              next ? "activated" : "deactivated"
            } successfully!`,
            {
              type: "success",
              position: "bottom-left",
            }
          );
          setTogglingId(null);
          invalidateLists();
        },
        onError: (err) => {
          setList((prev) =>
            prev.map((r) => (r.id === id ? { ...r, active: !next } : r))
          );
          setTogglingId(null);
          showError(
            err,
            `Failed to ${next ? "activate" : "deactivate"} the Business sector.`
          );
        },
      }
    );
  };

  const handleTabChange = (idx: number) => setActiveTab(idx);

  const canSave = formName.trim().length > 0 && !isSubmitting;

  return (
    <Box bg="white" borderRadius="16px" p={5} overflow="hidden">
      <Flex justifyContent="space-between" alignItems="center">
        <Box>
          <H2 fontSize="3">Sectors</H2>
          <Paragraph color="gray.60">
            Configure for capturing previous income and employment history here.
          </Paragraph>
        </Box>
        <Button Icon={FaPlus} iconSize="16px" onClick={openAdd}>
          Add
        </Button>
      </Flex>

      {isLoading ? (
        <Paragraph color="gray.60" mt={3}>
          Loading…
        </Paragraph>
      ) : isError ? (
        <Paragraph color="red.60" mt={3}>
          Failed to load business sectors.
        </Paragraph>
      ) : (
        <>
          <CustomTabs
            variant="segmented"
            tabs={[
              {
                label: (
                  <>
                    <HiOutlineSquares2X2 size={16} />
                    All
                  </>
                ),
                content: null,
              },
              {
                label: (
                  <>
                    <HiOutlineUser size={16} />
                    Individual
                  </>
                ),
                content: null,
              },
              {
                label: (
                  <>
                    <HiOutlineBuildingOffice2 size={16} />
                    Entity
                  </>
                ),
                content: null,
              },
            ]}
            customMarginLeft="0"
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          <div style={headStyle}>
            <Paragraph></Paragraph>
            <Paragraph color="gray.60">Risk level</Paragraph>
            <Paragraph color="gray.60">Last Updated</Paragraph>
            <Paragraph></Paragraph>
            <Paragraph color="gray.60">Active</Paragraph>
          </div>

          {list.length === 0 ? (
            <Paragraph color="gray.60" px={4} py={4}>
              No sectors found.
            </Paragraph>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              style={{
                borderTop: "1px solid #F3F4F6",
                opacity: isReordering ? 0.6 : 1,
                pointerEvents: isReordering ? "none" : "auto",
              }}
            >
              {list.map((item) => (
                <Row
                  key={String(item.id)}
                  item={item}
                  onToggleActive={onToggleActive}
                  onEdit={openEdit}
                  isDragging={dragState.current.draggingId === item.id}
                  isToggling={togglingId === item.id}
                  draggableProps={{
                    draggable: true,
                    onDragStart: handleDragStart(item.id),
                    onDragOver: handleDragOver(item.id),
                    onDragEnter: handleDragEnter(item.id),
                    onDragEnd: handleDragEnd,
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      <Slider isOpen={isFormOpen} onRequestClose={closeForm} width="600px">
        <Box px={4} py={3}>
          <H2 mb={4}>{formMode === "add" ? "Add Sector" : "Edit Sector"}</H2>

          <Box mb={3}>
            <Paragraph mb={1} fontWeight="600">
              Sector Name
            </Paragraph>
            <Input
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Enter sector name"
              isDisabled={isSubmitting}
            />
          </Box>

          <Box mb={3}>
            <Paragraph mb={1} fontWeight="600">
              Type
            </Paragraph>
            <Select
              options={typeOptions as any}
              value={typeOptions.find((o) => o.value === formType)}
              onChange={(opt: any) =>
                setFormType(
                  (opt?.value ?? "individual") as "individual" | "entity"
                )
              }
              width="100%"
              isDisabled={isSubmitting || scope !== "all"}
            />
            {scope !== "all" ? (
              <Paragraph color="gray.60" fontSize="1" mt={1}>
                Type locked to “{scope}” by the selected tab.
              </Paragraph>
            ) : null}
          </Box>

          <Box mb={3}>
            <Paragraph mb={1} fontWeight="600">
              Risk level
            </Paragraph>
            <Select
              options={riskOptions as any}
              value={riskOptions.find((o) => o.value === formRisk)}
              onChange={(opt: any) =>
                setFormRisk((opt?.value ?? "medium") as RiskLower)
              }
              width="100%"
              isDisabled={isSubmitting}
            />
          </Box>

          <Box mb={5}>
            <Paragraph mb={1} fontWeight="600">
              Active
            </Paragraph>
            <Toggle
              isChecked={formActive}
              onChange={(e: any) => setFormActive(e.target.checked)}
            />
          </Box>

          <Flex justifyContent="space-between" alignItems="center">
            {formMode === "edit" ? (
              <Button
                variant="danger"
                Icon={FaTrash}
                iconSize="12px"
                onClick={() => setIsDeleteModalOpen(true)}
                isDisabled={isSubmitting}
                iconColor="white"
              >
                Delete
              </Button>
            ) : (
              <span />
            )}

            <Flex gap="12" alignItems="center">
              <Button
                variant="secondary"
                onClick={closeForm}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={formMode === "add" ? handleCreate : handleUpdate}
                isDisabled={!canSave}
              >
                {isSubmitting ? "Saving…" : "Save"}
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Slider>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete business sector?"
        content="This will permanently remove the selected business sector."
        onModalConfirmClick={() => {
          if (isSubmitting) return;

          handleDelete();
        }}
        secondaryButtonText="Cancel"
        primaryButtonText={isSubmitting ? "Deleting…" : "Delete"}
        primaryButtonVariant="danger"
        hasSecondaryButton
        width="450px"
        doubleClick={doubleClickOnDelete}
      />
    </Box>
  );
};

export default SettingsPropertiesSectors;
